import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRightLeft, Trash2, Video, ExternalLink } from 'lucide-react';
import '../DashboardScore.css';
import ClubSelector from './ClubSelector';
import { eventsService } from '../../../../../../services';
import TransferOwnershipModal from './TransferOwnershipModal';

const EditEvent = ({
    isOpen,
    closeModal,
    event,
    onEventUpdated,
    initialView = 'details',
    canEditDetails = true,
    canManageMedia = true,
    onTransferChanged
}) => {
    const [eventData, setEventData] = useState({
        name: '',
        clubId: '',
        clubName: '',
        eventDate: '',
        eventTime: ''
    });
    const [activeView, setActiveView] = useState(!canEditDetails || initialView === 'media' ? 'media' : 'details');
    const [eventImages, setEventImages] = useState([]);
    const [eventVideos, setEventVideos] = useState([]);
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const [isLoadingVideos, setIsLoadingVideos] = useState(false);
    const [isUploadingImages, setIsUploadingImages] = useState(false);
    const [isSavingVideo, setIsSavingVideo] = useState(false);
    const [draggingImageId, setDraggingImageId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [mediaTab, setMediaTab] = useState('photos');
    const [videoUrl, setVideoUrl] = useState('');
    const [videoDescription, setVideoDescription] = useState('');
    const [previewImage, setPreviewImage] = useState(null);
    const [transferModalOpen, setTransferModalOpen] = useState(false);

    useEffect(() => {
        if (isOpen && event) {
            setEventData({
                name: event.name || '',
                clubId: event.clubId || '',
                clubName: event.clubName || '',
                eventDate: event.eventDate ? new Date(event.eventDate).toISOString().split('T')[0] : '',
                eventTime: event.eventTime || ''
            });
        }
    }, [isOpen, event]);

    useEffect(() => {
        if (isOpen) {
            setActiveView(!canEditDetails || initialView === 'media' ? 'media' : 'details');
            if (initialView === 'media') {
                setMediaTab('photos');
            }
        }
    }, [canEditDetails, initialView, isOpen, event?.eventId]);

    useEffect(() => {
        const loadEventImages = async () => {
            if (!isOpen || !event?.eventId) return;

            setIsLoadingImages(true);
            try {
                const images = await eventsService.getEventImages(event.eventId);
                setEventImages(Array.isArray(images) ? images : []);
            } catch (error) {
                console.error('Error loading event images:', error);
                setEventImages([]);
            } finally {
                setIsLoadingImages(false);
            }
        };

        loadEventImages();
    }, [isOpen, event?.eventId]);

    useEffect(() => {
        const loadEventVideos = async () => {
            if (!isOpen || !event?.eventId) return;

            setIsLoadingVideos(true);
            try {
                const videos = await eventsService.getEventVideos(event.eventId);
                setEventVideos(Array.isArray(videos) ? videos : []);
            } catch (error) {
                console.error('Error loading event videos:', error);
                setEventVideos([]);
            } finally {
                setIsLoadingVideos(false);
            }
        };

        loadEventVideos();
    }, [isOpen, event?.eventId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEventData(prev => ({ ...prev, [name]: value }));
    };

    const handleClubSelect = (club) => {
        setEventData(prev => ({
            ...prev,
            clubId: club.clubId,
            clubName: club.name
        }));
    };

    const submitForm = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);

        // Convert eventDate to dd/mm/yyyy for display consistency
        const formattedEventData = {
            ...eventData,
            eventDate: new Date(eventData.eventDate).toISOString().split('T')[0]
        };

        try {
            const updatedEvent = await eventsService.updateEvent(event.eventId, formattedEventData);
            onEventUpdated(updatedEvent);
            closeModal();
        } catch (error) {
            console.error('Error updating event:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!event?.eventId || files.length === 0 || isUploadingImages) return;

        setIsUploadingImages(true);
        try {
            const formData = new FormData();
            files.forEach(file => formData.append('files', file));

            const result = await eventsService.uploadEventImages(event.eventId, formData);
            const uploaded = result?.images || [];
            if (uploaded.length > 0) {
                setEventImages(prev => [...uploaded, ...prev]);
            }
        } catch (error) {
            console.error('Error uploading event images:', error);
        } finally {
            setIsUploadingImages(false);
            e.target.value = '';
        }
    };

    const handleDeleteImage = async (imageId) => {
        if (!event?.eventId || !imageId) return;

        try {
            await eventsService.deleteEventImage(event.eventId, imageId);
            setEventImages(prev => prev.filter(image => image.eventImageId !== imageId));
        } catch (error) {
            console.error('Error deleting event image:', error);
        }
    };

    const handleAddVideo = async () => {
        if (!event?.eventId || !videoUrl.trim() || isSavingVideo) return;

        setIsSavingVideo(true);
        try {
            const video = await eventsService.addEventVideo(event.eventId, {
                videoUrl: videoUrl.trim(),
                description: videoDescription.trim()
            });
            setEventVideos(prev => [video, ...prev]);
            setVideoUrl('');
            setVideoDescription('');
        } catch (error) {
            console.error('Error adding event video:', error);
        } finally {
            setIsSavingVideo(false);
        }
    };

    const handleDeleteVideo = async (videoId) => {
        if (!event?.eventId || !videoId) return;

        try {
            await eventsService.deleteEventVideo(event.eventId, videoId);
            setEventVideos(prev => prev.filter(video => video.eventVideoId !== videoId));
        } catch (error) {
            console.error('Error deleting event video:', error);
        }
    };

    const handleImageDragStart = (imageId) => {
        setDraggingImageId(imageId);
    };

    const handleImageDragOver = (e) => {
        e.preventDefault();
    };

    const handleImageDrop = async (targetImageId) => {
        if (!draggingImageId || draggingImageId === targetImageId || !event?.eventId) {
            setDraggingImageId(null);
            return;
        }

        const currentImages = [...eventImages];
        const fromIndex = currentImages.findIndex(img => img.eventImageId === draggingImageId);
        const toIndex = currentImages.findIndex(img => img.eventImageId === targetImageId);

        if (fromIndex < 0 || toIndex < 0) {
            setDraggingImageId(null);
            return;
        }

        const reorderedImages = [...currentImages];
        const [movedImage] = reorderedImages.splice(fromIndex, 1);
        reorderedImages.splice(toIndex, 0, movedImage);

        setEventImages(reorderedImages);
        setDraggingImageId(null);

        try {
            await eventsService.reorderEventImages(event.eventId, reorderedImages.map(img => img.eventImageId));
        } catch (error) {
            console.error('Error reordering event images:', error);
            setEventImages(currentImages);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="create-event-container active">
            <div className="create-event-overlay" onClick={closeModal}></div>
            <div className="create-event-box">
                <div className="modal-header">
                    <div>
                        <h2>{activeView === 'media' ? 'Event Media' : 'Edit Event'}</h2>
                        <p className="create-event-subtitle">
                            {activeView === 'media'
                                ? canManageMedia
                                    ? 'Manage photos and video links for this event.'
                                    : 'Browse the event gallery and video links for this event.'
                                : 'Update the event details, timing, and venue information.'}
                        </p>
                    </div>
                </div>
                <div className="edit-event-view-toggle">
                    {canEditDetails && (
                        <button
                            type="button"
                            className={`edit-event-view-btn ${activeView === 'details' ? 'active' : ''}`}
                            onClick={() => setActiveView('details')}
                        >
                            Details
                        </button>
                    )}
                    <button
                        type="button"
                        className={`edit-event-view-btn ${activeView === 'media' ? 'active' : ''}`}
                        onClick={() => setActiveView('media')}
                    >
                        Media
                    </button>
                </div>
                <form onSubmit={submitForm}>
                    {activeView === 'details' && canEditDetails ? (
                        <div className="row">
                            <div className="col-12">
                                <div className="form-group">
                                    <label className='dashboard-form-label'>Name</label>
                                    <div className="dashboard-form-field">
                                        <input
                                            type="text"
                                            name="name"
                                            value={eventData.name}
                                            onChange={handleInputChange}
                                            placeholder='Event Name'
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="form-group">
                                    <label className='dashboard-form-label'>Club</label>
                                    <ClubSelector
                                        selectedClub={eventData.clubId ? {
                                            id: eventData.clubId,
                                            name: eventData.clubName
                                        } : null}
                                        onClubSelect={handleClubSelect}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="form-group">
                                    <label className='dashboard-form-label'>Date</label>
                                    <div className="dashboard-form-field">
                                        <input
                                            type="date"
                                            name="eventDate"
                                            value={eventData.eventDate}
                                            onChange={handleInputChange}
                                            className='create-event-input'
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="form-group">
                                    <label className='dashboard-form-label'>Time</label>
                                    <div className="dashboard-form-field">
                                        <input
                                            type="time"
                                            name="eventTime"
                                            value={eventData.eventTime}
                                            onChange={handleInputChange}
                                            className='create-event-input'
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="edit-event-media-panel">
                            <div className="edit-event-view-toggle">
                                <button
                                    type="button"
                                    className={`edit-event-view-btn ${mediaTab === 'photos' ? 'active' : ''}`}
                                    onClick={() => setMediaTab('photos')}
                                >
                                    Photos
                                </button>
                                <button
                                    type="button"
                                    className={`edit-event-view-btn ${mediaTab === 'videos' ? 'active' : ''}`}
                                    onClick={() => setMediaTab('videos')}
                                >
                                    Videos
                                </button>
                            </div>
                            {mediaTab === 'photos' ? (
                                <>
                                    <div className="edit-event-media-upload">
                                        <div>
                                            <h4>Event gallery</h4>
                                            <p>
                                                {canManageMedia
                                                    ? 'Add photos now, then drag to set the order they appear in.'
                                                    : 'View the photo gallery for this event.'}
                                            </p>
                                        </div>
                                        {canManageMedia && (
                                            <label className="dashboard-default-btn edit-event-upload-btn" style={{ cursor: 'pointer' }}>
                                                {isUploadingImages ? 'Uploading...' : 'Upload photo(s)'}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    onChange={handleImageUpload}
                                                    style={{ display: 'none' }}
                                                    disabled={isUploadingImages}
                                                />
                                            </label>
                                        )}
                                    </div>
                            {isLoadingImages ? (
                                <p>Loading images...</p>
                            ) : (
                                <>
                                    {eventImages.length > 1 && canManageMedia && (
                                        <p className="edit-event-media-hint">Drag photos to reorder the gallery.</p>
                                    )}
                                    {!canManageMedia && (
                                        <p className="edit-event-media-hint edit-event-media-readonly">
                                            Only the event owner can change details or manage media. You can still browse everything here.
                                        </p>
                                    )}
                                    {eventImages.length === 0 ? (
                                        <div className="edit-event-media-empty">
                                            <h5>No event photos yet</h5>
                                            <p>
                                                {canManageMedia
                                                    ? 'Upload a cover image or build out the full event gallery here.'
                                                    : 'The owner has not added any event photos yet.'}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="edit-event-media-grid">
                                            {eventImages.map(image => (
                                                <div
                                                    key={image.eventImageId}
                                                    className="edit-event-media-card"
                                                    style={{
                                                        opacity: draggingImageId === image.eventImageId ? 0.55 : 1
                                                    }}
                                                    draggable={canManageMedia}
                                                    onDragStart={canManageMedia ? () => handleImageDragStart(image.eventImageId) : undefined}
                                                    onDragOver={canManageMedia ? handleImageDragOver : undefined}
                                                    onDrop={canManageMedia ? () => handleImageDrop(image.eventImageId) : undefined}
                                                >
                                                    <img
                                                        src={image.imageUrl}
                                                        alt={image.originalFileName || 'Event'}
                                                        className="edit-event-media-image"
                                                        onClick={() => setPreviewImage(image)}
                                                    />
                                                    {canManageMedia && (
                                                        <button
                                                            type="button"
                                                            className="edit-event-media-remove"
                                                            onClick={() => handleDeleteImage(image.eventImageId)}
                                                            aria-label="Remove photo"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                                </>
                            ) : (
                                <div className="edit-event-media-panel">
                                    <div className="edit-event-media-upload">
                                        <div>
                                            <h4>Event videos</h4>
                                            <p>
                                                {canManageMedia
                                                    ? 'Add YouTube, Vimeo, or hosted video links for highlights or walkthroughs.'
                                                    : 'Browse the event video links shared by the owner.'}
                                            </p>
                                        </div>
                                    </div>
                                    {canManageMedia && (
                                        <>
                                            <div className="row">
                                                <div className="col-lg-7">
                                                    <div className="form-group">
                                                        <label className='dashboard-form-label'>Video URL</label>
                                                        <div className="dashboard-form-field">
                                                            <input
                                                                type="url"
                                                                value={videoUrl}
                                                                onChange={(e) => setVideoUrl(e.target.value)}
                                                                placeholder="https://..."
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-5">
                                                    <div className="form-group">
                                                        <label className='dashboard-form-label'>Description</label>
                                                        <div className="dashboard-form-field">
                                                            <input
                                                                type="text"
                                                                value={videoDescription}
                                                                onChange={(e) => setVideoDescription(e.target.value)}
                                                                placeholder="Walkthrough or highlights"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="dashboard-default-btn"
                                                    onClick={handleAddVideo}
                                                    disabled={!videoUrl.trim() || isSavingVideo}
                                                >
                                                    {isSavingVideo ? 'Saving...' : 'Add Video'}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                    {!canManageMedia && (
                                        <p className="edit-event-media-hint edit-event-media-readonly">
                                            Only the event owner can add or remove videos.
                                        </p>
                                    )}
                                    {isLoadingVideos ? (
                                        <p>Loading videos...</p>
                                    ) : eventVideos.length === 0 ? (
                                        <div className="edit-event-media-empty">
                                            <h5>No event videos yet</h5>
                                            <p>
                                                {canManageMedia
                                                    ? 'Add a video link to show the event atmosphere or briefing.'
                                                    : 'The owner has not added any event videos yet.'}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="edit-event-video-list">
                                            {eventVideos.map((video) => (
                                                <div key={video.eventVideoId} className="edit-event-video-card">
                                                    <div>
                                                        <strong>{video.description || 'Event video'}</strong>
                                                        <a href={video.videoUrl} target="_blank" rel="noreferrer" className="edit-event-video-link">
                                                            <ExternalLink size={14} />
                                                            {video.videoUrl}
                                                        </a>
                                                    </div>
                                                    <div className="edit-event-video-actions">
                                                        <a
                                                            href={video.videoUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="edit-event-media-remove"
                                                            aria-label="Open video"
                                                        >
                                                            <Video size={16} />
                                                        </a>
                                                        {canManageMedia && (
                                                            <button
                                                                type="button"
                                                                className="edit-event-media-remove"
                                                                onClick={() => handleDeleteVideo(video.eventVideoId)}
                                                                aria-label="Remove video"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {activeView === 'details' && canEditDetails ? (
                        <div className="edit-event-transfer-panel">
                            <div>
                                <h4>Ownership</h4>
                                <p>Need another registered shooter or friend to take over? Send a transfer request and let them accept it explicitly.</p>
                            </div>
                            <button
                                type="button"
                                className="dashboard-card-outline-btn"
                                onClick={() => setTransferModalOpen(true)}
                            >
                                <ArrowRightLeft size={16} />
                                Transfer Event
                            </button>
                        </div>
                    ) : null}
                    <div className="modal-ctas">
                        <button type="button" className="dashboard-outline-btn me-2" onClick={closeModal}>
                            Cancel
                        </button>
                        {activeView === 'details' && canEditDetails ? (
                            <button type="submit" className="dashboard-default-btn" disabled={isSubmitting}>
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </button>
                        ) : (
                            <button type="button" className="dashboard-default-btn" onClick={closeModal}>
                                Done
                            </button>
                        )}
                    </div>
                </form>
            </div>
            {previewImage && createPortal(
                <div className="edit-event-lightbox" onClick={() => setPreviewImage(null)}>
                    <button
                        type="button"
                        className="edit-event-lightbox-close"
                        onClick={() => setPreviewImage(null)}
                        aria-label="Close image preview"
                    >
                        x
                    </button>
                    <img
                        src={previewImage.imageUrl}
                        alt={previewImage.originalFileName || 'Event'}
                        className="edit-event-lightbox-image"
                    />
                </div>,
                document.body
            )}
            <TransferOwnershipModal
                isOpen={transferModalOpen}
                event={event}
                onClose={() => setTransferModalOpen(false)}
                onTransferChanged={onTransferChanged}
            />
        </div>
    );
};

export default EditEvent;
